/**
 * Home Data Transfer Object
 * Represents a normalized Home entity with validation
 */

import { RoomDTO } from './RoomDTO';

export interface IHomeData {
  uid?: string;
  id?: string;
  name?: string;
  userUid?: string;
  deviceUids?: string[];
  gatewayUids?: string[];
  rooms?: any[]; // Can be room IDs (strings) or full room objects
}

export class HomeDTO {
  readonly uid: string;
  readonly name: string;
  readonly userUid: string;
  readonly deviceUids: string[];
  readonly gatewayUids: string[];
  readonly rooms: RoomDTO[]; // Always full RoomDTOs after normalization
  readonly roomIds: string[]; // Room IDs for reference

  constructor(data: IHomeData) {
    this.uid = this.validateRequired(data.uid || data.id, 'uid');
    this.name = this.validateRequired(data.name, 'name');
    this.userUid = this.validateRequired(data.userUid, 'userUid');
    this.deviceUids = Array.isArray(data.deviceUids) ? data.deviceUids : [];
    this.gatewayUids = Array.isArray(data.gatewayUids) ? data.gatewayUids : [];

    // Handle rooms: if they're objects, convert to DTOs; if strings, store as IDs
    if (Array.isArray(data.rooms) && data.rooms.length > 0) {
      // Check if first item is a string (room ID) or object (room data)
      if (typeof data.rooms[0] === 'string') {
        // Room IDs only
        this.roomIds = data.rooms as string[];
        this.rooms = [];
      } else {
        // Full room objects
        this.rooms = data.rooms.map(r => new RoomDTO(r));
        this.roomIds = this.rooms.map(r => r.uid);
      }
    } else {
      this.rooms = [];
      this.roomIds = [];
    }
  }

  private validateRequired(value: any, fieldName: string): string {
    if (value === undefined || value === null || value === '') {
      throw new Error(`HomeDTO: ${fieldName} is required`);
    }
    return String(value);
  }

  /**
   * Create DTO from API response
   */
  static fromApiResponse(apiData: IHomeData): HomeDTO {
    return new HomeDTO(apiData);
  }

  /**
   * Create multiple DTOs from API response array
   */
  static fromApiResponseArray(apiDataArray: any): HomeDTO[] {
    if (!Array.isArray(apiDataArray)) {
      console.warn('HomeDTO.fromApiResponseArray: expected array, got:', typeof apiDataArray);
      return [];
    }
    return apiDataArray.map(data => HomeDTO.fromApiResponse(data));
  }

  /**
   * Convert to plain object for store
   */
  toStoreEntity(): {
    uid: string;
    name: string;
    userUid: string;
    deviceUids: string[];
    gatewayUids: string[];
  } {
    return {
      uid: this.uid,
      name: this.name,
      userUid: this.userUid,
      deviceUids: this.deviceUids,
      gatewayUids: this.gatewayUids,
    };
  }
}
